import { Link as CapraLink } from '@capra/core';
import NextLink from 'next/link';
import styled, { createGlobalStyle } from 'styled-components';

export const BlockLink = styled(CapraLink).attrs({ as: NextLink })`
  display: block;
  font-size: 12px;
`;

export const OrganizationNav = styled.nav`
  --nav-item-bg-active: #ccf5f5;
  --nav-item-bg-hover: #dae2e3;
  --nav-item-height: 32px;
  --nav-item-text-color: #595959;
  --nav-item-text-color-hover: rgba(0, 0, 0, 0.85);
  --nav-item-text-color-active: #024c4c;
  --nav-item-icon-color: rgba(0, 0, 0, 0.45);
  --nav-item-icon-color-hover: rgba(0, 0, 0, 0.85);
  --nav-item-icon-color-active: #024c4c;
  --collapsed-nav-item-height: 24px;

  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const OrganizationMenu = styled.div`
  height: 100%;

  .ant-menu-inline {
    width: 180px;
  }

  .ant-menu-inline-collapsed {
    width: 44px;
  }

  .ant-menu.ant-menu-root {
    border-right: 0;
    background-color: rgba(245, 245, 245, 1);
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 8px;
    transition: all 0.3s ease-in-out;
  }

  .ant-menu-sub,
  .ant-menu-item {
    background-color: inherit;
  }

  .ant-menu-item,
  .ant-menu-submenu-selected,
  .ant-menu-submenu-title,
  .ant-menu-item-selected {
    font-size: 12px;
    &:active {
      background-color: inherit;
    }
  }

  &&& .ant-menu-item-only-child {
    &:hover {
      a {
        color: var(--nav-item-text-color-hover) !important;
      }
    }
  }

  &&& .ant-menu-title-content {
    color: var(--nav-item-text-color);
    &:hover {
      color: var(--nav-item-text-color-hover);
      .anticon-info-circle {
        color: var(--nav-item-text-color-hover) !important;
      }
    }
  }

  &&& .ant-menu-item,
  .ant-menu-submenu-title {
    align-items: center;
    display: flex;
    --top-margin: 0px;

    height: var(--nav-item-height) !important; /* need important to override Ant styles */
    margin: var(--top-margin) 0 2px 0 !important; /* make sure gap between items isn't too large - Ant default is 8px */
  }

  &&& .ant-menu > li:nth-child(4) {
    --top-margin: auto;
  }

  &&& .ant-menu-item {
    padding-left: 6px !important;
  }

  &&&  .ant-menu-sub .ant-menu-item {
    padding-left: 33px !important; /* not divisible by 4 because of the 1px border */
    width: calc(100% - 1px - 16px); /* items are indented, so width should be that minus 1px for border */
  }

  &&& .ant-menu-submenu-active .ant-menu-submenu-title,
  .ant-menu-submenu-inline.ant-menu-submenu-active .ant-menu-submenu-title:hover {
    background: var(--nav-item-bg-hover) !important;
    border: none !important;
    border-radius: 6px;
    cursor: default;
    a {
      cursor: pointer;
    }
  }

  &&& .ant-menu-submenu,
  .ant-menu-item {
    cursor: default;
    &:hover {
      cursor: pointer;
      a {
        cursor: pointer;
        color: var(--nav-item-text-color-hover);
      }
      svg {
        fill: var(--nav-item-text-color-hover);
      }
    }

    a {
      color: var(--nav-item-text-color);
    }
    svg {
      fill: var(--nav-item-icon-color);
    }
  }

  &&& .ant-menu-item {
    a {
      color: var(--nav-item-text-color) !important;
    }
    &:hover {
      a {
        color: var(--nav-item-text-color-hover) !important;
      }
    }
  }

  &&& .ant-menu-submenu-title {
    padding-left: 6px !important;
    padding-right: 0;
  }

  &&& .ant-menu-item-selected,
  .ant-menu-item:active,
  .ant-menu-submenu-selected .ant-menu-submenu-title {
    background: var(--nav-item-bg-active) !important;
    cursor: default;
    &:hover {
      a {
        color: var(--nav-item-text-color-hover) !important;
        cursor: pointer;
      }
      svg {
        fill: var(--nav-item-text-color-hover) !important;
      }
    }
    a {
      color: var(--nav-item-text-color-active) !important;
    }
    svg {
      fill: var(--nav-item-icon-color-active);
    }
    span {
      color: var(--nav-item-text-color-active) !important;
      a {
        color: var(--nav-item-text-color-active) !important;
      }
    }
    border: none !important;
    border-radius: 6px;
    color: var(--nav-item-text-color-active) !important;
  }

  &&& .ant-menu-submenu-open.ant-menu-submenu-selected:has(.ant-menu-item-selected) .ant-menu-submenu-title:first-child {
    background: none !important;
    a {
      color: var(--nav-item-text-color-hover) !important;
    }
    svg {
      fill: var(--nav-item-text-color-hover) !important;
    }
    &:hover {
      background: var(--nav-item-bg-hover) !important;
    }
  }

  &&& .ant-menu-item:after {
    border: none;
  }

  .ant-menu .ant-menu-item .ant-menu-item-icon +span {
    margin-left: 10px;
  }

  &&& .ant-menu-item-icon,
  .ant-menu-submenu-title .anticon {
    font-size: 16px;
    line-height: var(--nav-item-height) !important; /* important to override Ant */
  }

  &&& .anticon-double-left {
    padding-top: 0px !important;
  }
  &&& .anticon-double-left,
  .anticon-double-right {
    color: var(--nav-item-icon-color);
    &:hover {
      color: var(--nav-item-icon-color-hover) !important;
    }
  }

  &&& .ant-menu-item-icon,
  .anticon-info-circle {
    color: var(--nav-item-icon-color);
    &:hover {
      color: var(--nav-item-text-color-hover) !important;
    }
  }

  &&& .ant-menu-item:hover {
    background: var(--nav-item-bg-hover) !important;
    border: none !important;
    border-radius: 6px;
    font-size: 12px;
    .anticon-info-circle {
      color: var(--nav-item-text-color-hover) !important;
    }
  }

  &&& .ant-menu-item {
    &:hover {
      span {
        color: var(--nav-item-text-color-hover) !important;
      }
    }
  }

  &&& .ant-menu-inline-collapsed {
    .ant-menu-submenu-title,
    .ant-menu-item {
      &:hover {
        background: var(--nav-item-bg-hover) !important;
      }
      align-items: center;
      display: flex;
      --top-margin: 0px;

      height: var(--collapsed-nav-item-height) !important; /* need important to override Ant styles */
      margin: var(--top-margin) 0 10px 0 !important; /* make sure gap between items isn't too large - Ant default is 8px */
    }
    .ant-menu-item-icon,
    .anticon-info-circle {
      padding-top: 4px;
    }
    .ant-menu-item-only-child {
      &:first-child {
        margin-top: 4px !important;
      }
    }
    .ant-menu-item-icon {
      font-size: 16px;
      display: inline-block;

      > svg {
        width: 16px;
        height: 16px;
      }
    }
  }
`;

export const GlobalPopupMenuStyled = createGlobalStyle`
  &&& .ant-menu-submenu-popup {
    .ant-menu-item {
      margin-left: 6px !important;
      margin-right: 6px !important;
    }

    .ant-menu-item a:hover,
    .ant-menu-item-selected a {
      color: rgba(0, 0, 0, 0.85) !important;
    }

    .ant-menu-item:hover {
      background-color: #dae2e3 !important;
      border: none !important;
      border-radius: 6px;
      color: rgba(0, 0, 0, 0.85);
    }

    .ant-menu-item-selected,
    .ant-menu-item:active,
    .ant-menu-submenu-selected .ant-menu-submenu-title {
      border: none !important;
      border-radius: 6px;
    }
  }
`;
